import { Component, Output, EventEmitter, Input, OnInit } from '@angular/core';
import { tvdData, custTile, PresentationPreviewTile, TvdValuePillar, EroiIdToPillarMapping, TvdMetricGroup, eroiIds } from '../../types/tvd-types';
import { eroiService } from '../../services/eroi-metric.service';
import { Subscription } from 'rxjs';
import { UIPreferencesService } from 'src/app/core/services/ui-preferences.service';
import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';

@Component({
    selector: 'tvd-presentation-preview',
    templateUrl: './tvd-presentation-preview.component.html',
    styleUrls: ['./tvd-presentation-preview.component.scss'],
    standalone: false
})
export class TvdPresentationPreviewComponent implements OnInit {
  @Output() clearSelection = new EventEmitter<void>();
  @Output() saveSelection = new EventEmitter<void>();
  @Output() exitPreview = new EventEmitter<void>();

  //For Distributor, this will be sent as an Input. Currently assumed to be "default_tvd" group id
  @Input() tvdMetricGroupData: TvdMetricGroup | undefined;
  // For other divisions, selectedItemsDict will be sent as an Input
  @Input() selectedItemsDict: Map<string, [data:tvdData, isSelected:boolean, isAddedToPres:boolean]> | undefined;
  @Input() customTilesDict: Map<string, custTile> | undefined;

  private displayInRoundedValuesSubscription: Subscription | undefined;

  selectedTilesArray: PresentationPreviewTile[] = [];
  businessOutcomesTiles: PresentationPreviewTile[] = [];
  operationalPerformanceTiles: PresentationPreviewTile[] = [];
  environmentalImpactTiles: PresentationPreviewTile[] = [];
  renderedSections: {key: string, name:string, class: string, background: string, tiles: PresentationPreviewTile[]}[] = [];
  colLayoutWidth: string = 'w-full'; 
  displayRoundedValues: boolean = false;

  constructor(
    private _eroiService: eroiService,
    private _uiPreferencesService: UIPreferencesService,
    private _currencyUomService: CurrencyUomService
  ) { }

  ngOnInit(): void {
    this.displayInRoundedValuesSubscription = this._uiPreferencesService.displayRoundedValuesObservable.subscribe(f => this.displayRoundedValues = f);
    this.buildSelectedTilesArray();
    this.setColumnWidth();
    this.getRenderedSections();
  }

  // Getter to organize tiles into their respective sections for easier rendering in the template
  getRenderedSections() {
    this.renderedSections = [
      {
        key: 'business',
        name: TvdValuePillar.BusinessOutcomes,
        class: 'business-outcomes-preview',
        background: 'bg-gray',
        tiles: this.businessOutcomesTiles
      },
      {
        key: 'operational',
        name: TvdValuePillar.OperationalPerformance,
        class: 'operational-performance-preview',
        background: 'bg-deep-blue',
        tiles: this.operationalPerformanceTiles
      },
      {
        key: 'environmental',
        name: TvdValuePillar.EnvironmentalImpact,
        class: 'environmental-impact-preview',
        background: 'bg-neptune-color-green-500',
        tiles: this.environmentalImpactTiles
      }
    ].filter(s => s.tiles?.length > 0);
  }


  buildSelectedTilesArray() {
    this.businessOutcomesTiles = [];
    this.operationalPerformanceTiles = [];
    this.environmentalImpactTiles = [];

    if(this.selectedItemsDict) {
      this.selectedItemsDict.forEach((value, key) => {
        // If a metric is explicitly selected or all tiles should be shown (no tiles are explicitly selected in UI)
        if(value[1]) { 
          if(this.isDataZeroOrEmpty(value[0])) {
            return; // skip if data is zero or empty
          }

          let title = this._eroiService.mapIdToTitle(key); // TODO: Remove once all references are modified to pass the EROI ID instead of title to avoid the need for mapping in this component and spec test modified.
          let valueUnits = this._eroiService.mapIdToUnits(key);
          let costCurrencyCode = this._currencyUomService.getDisplayedCurrency();
          let iconPath = this._eroiService.mapIdToMetricIconPath(key);
          let valuePillar = EroiIdToPillarMapping.get(key) ?? TvdValuePillar.BusinessOutcomes; // first try direct mapping, then fallback to default

          // If title and units are returned as '', its a custom tile
          // TODO: Consider refactoring to use a unique identifier for custom tiles instead of relying on title and units being blank which could lead to issues if there are legitimate metrics with blank titles or units or if there are custom tiles with titles and units. This would also eliminate the need for mapping in this component and spec test modified.
          if(title === '' && valueUnits === '') {
            const customTile = this.customTilesDict?.get(key);
            if(customTile) {
              title = customTile.title; // TODO: Consider using a unique identifier for custom tiles instead of relying on title for better reliability in case of title changes or duplicates
              valueUnits = customTile.unit;
              iconPath = this._eroiService.mapImpactCategoryToIconPath(customTile.impactCategory);
              valuePillar = customTile.valuePillar;
            }
          }

          const tile: PresentationPreviewTile = {
            title: title, // TODO: Consider using a unique identifier for custom tiles instead of relying on title for better reliability in case of title changes or duplicates
            valueUnit: valueUnits,
            iconPath: iconPath,
            valuePillar: valuePillar,
            costCurrencyCode: costCurrencyCode,
            monetaryContribution: value[0].monetaryContribution ?? '0',
            valueContribution: value[0].valueContribution ?? '0',
          };

          // Push to correct bucket based on value pillar
          switch(valuePillar) {
            case TvdValuePillar.BusinessOutcomes:
              this.businessOutcomesTiles.push(tile);
              break;
            case TvdValuePillar.OperationalPerformance:
              this.operationalPerformanceTiles.push(tile);
              break;
            case TvdValuePillar.EnvironmentalImpact:
              this.environmentalImpactTiles.push(tile);
              break;
          }
        }
      });
    }
    else if(this.tvdMetricGroupData) {
      this.tvdMetricGroupData.indvMetrics
        // Exclude composite metrics
        .filter(metric => metric.id!= eroiIds.SOLID_WASTE)
        .forEach(metric => {
          let title = this._eroiService.mapIdToTitle(metric.id);
          let valueUnits = this._eroiService.mapIdToUnits(metric.id);
          let costCurrencyCode = this._currencyUomService.getDisplayedCurrency();
          let iconPath = this._eroiService.mapIdToMetricIconPath(metric.id);
          let valuePillar = EroiIdToPillarMapping.get(metric.id) ?? TvdValuePillar.BusinessOutcomes; // first try direct mapping, then fallback to default

          const tile: PresentationPreviewTile = {
            title: title,
            valueUnit: valueUnits,
            iconPath: iconPath,
            valuePillar: valuePillar,
            costCurrencyCode: costCurrencyCode,
            monetaryContribution: metric.monetaryContribution ?? '0',
            valueContribution: metric.valueContribution ?? '0',
          };

          // Push to correct bucket based on value pillar
          switch(valuePillar) {
            case TvdValuePillar.BusinessOutcomes:
              this.businessOutcomesTiles.push(tile);
              break;
            case TvdValuePillar.OperationalPerformance:
              this.operationalPerformanceTiles.push(tile);
              break;
            case TvdValuePillar.EnvironmentalImpact:
              this.environmentalImpactTiles.push(tile);
              break;
          }
      });
    }
  }

  onSaveSelection() {
    this.saveSelection.emit();
    this.exitPreview.emit();
  }

  onClearSelection() {
    this.clearSelection.emit();
    this.exitPreview.emit();
  }

  onExitPreview() {
    this.exitPreview.emit();
  }

  isDataZeroOrEmpty(tvdData: tvdData): boolean {
    return tvdData.monetaryContribution == '0' && (tvdData.valueContribution == '0' || tvdData.valueContribution === '');
  }


  // Helper to determine column style layout based on available value pillars.
  setColumnWidth() {
    const {
      businessOutcomesTiles: bo,
      operationalPerformanceTiles: op,
      environmentalImpactTiles: ei
    } = this;

    // Count how many pillars are present
    const present = [bo, op, ei].filter(n => n.length > 0).length;

    if (present === 2) {
      this.colLayoutWidth = 'w-1/2';
    } else if (present === 3) {
      this.colLayoutWidth = 'w-1/3';
    } else {
      this.colLayoutWidth = 'w-full';
    }
  }
}
  